<script setup lang="ts">
import { computed } from "vue";
import { useDtfStore } from "../../store";
import type { Standard } from "../../types";
import Headline from "../components/Headline.vue";
import Section from "../components/Section.vue";
import { List, ListItem } from "../components/List";
import RedrawNotes from "../../RedrawNotes.vue";
import PriceLabel from "../components/PriceLabel.vue";
import CustomComparison from "./CustomComparison.vue";

defineProps<{
  artwork: Standard.Artwork;
  reorderEnabled: boolean;
  index: number;
}>();

const store = useDtfStore();
const showRedrawNote = computed(() => store.showRedrawUI);
</script>

<template>
  <Headline
    class="custom__headline"
    :class="{
      'custom__headline--large-padding': reorderEnabled
    }"
  >
    <template #title>
      <div class="custom__title">
        Custom edits, done for you.
        <PriceLabel
          :artwork="artwork"
          :reorder-enabled="reorderEnabled"
          :show-retail-price="false"
        />
      </div>
    </template>
    <template #subtitle>
      <div class="custom__subtitle">
        Tell us what you want! Our expert team will adjust your design and make sure it's just right
        for printing.
      </div>
    </template>
  </Headline>

  <Section class="custom__list-section">
    <List long>
      <ListItem :show-monetization="true">Same-day turnaround</ListItem>
      <ListItem :show-monetization="true">Precise color changes</ListItem>
      <ListItem :show-monetization="true">Custom background removal</ListItem>
      <ListItem :show-monetization="true">Fine-tuned adjustments</ListItem>
      <ListItem kind="highlight">
        <a href="/jiffy-transfers-FAQs#design-quality-guarantee" target="_blank">
          PrintPerfect Guarantee
        </a>
        included
      </ListItem>
    </List>
  </Section>

  <template v-if="showRedrawNote">
    <RedrawNotes
      :index="index"
      :enhancement-note="artwork.enhancementNote"
      :disabled="!artwork.enhancementCompleted"
    />
  </template>

  <Section class="custom__comparison">
    <CustomComparison />
  </Section>
</template>

<style scoped lang="scss">
@import "../ArtworkQualityConfigPanel.scss";

.custom {
  &__headline {
    padding-right: 100px;

    &--large-padding {
      padding-right: 140px;
    }
  }

  &__title {
    display: flex;
    gap: 30px;
    justify-content: space-between;
  }

  &__list-section {
    margin-bottom: 16px;
  }

  &__subtitle {
    @include with-quality-container {
      padding-right: 200px;
    }
  }

  &__comparison {
    margin-top: 8px;
  }
}
</style>
